import { useCallback, useMemo, useContext } from "react";
import { AppPreferencesContext } from "./AppPreferencesProvider";
import type {
  AppPreferencesContextType,
  SeverityMappingItem,
} from "../types/AppPreferencesProvider";

export interface ScoreItem {
  score: number;
  severity: number;
}

interface SeveritiesLabels {
  SEVERITY: string;
  PRIORITY: string;
  SCORE: string;
}

interface SeverityFieldsEnumType {
  NAME: string;
  VALUE: string;
  COLOR: string;
  TEXT_COLOR: string;
  SCORE: string;
}

const severitiesLabels: SeveritiesLabels = {
  SEVERITY: "Severity",
  PRIORITY: "Priority",
  SCORE: "Score",
};
export const scores: ScoreItem[] = [
  {
    score: 0,
    severity: 0,
  },
  {
    score: 1,
    severity: 0,
  },
  {
    score: 2,
    severity: 0,
  },
  {
    score: 3,
    severity: 0,
  },
  {
    score: 4,
    severity: 1,
  },
  {
    score: 5,
    severity: 1,
  },
  {
    score: 6,
    severity: 1,
  },
  {
    score: 7,
    severity: 2,
  },
  {
    score: 8,
    severity: 2,
  },
  {
    score: 9,
    severity: 3,
  },
  {
    score: 10,
    severity: 3,
  },
].reverse();

// Default Severity Mapping for Seemplicity
const severityMapping: SeverityMappingItem[] = [
  {
    name: "critical",
    value: 3,
    score: 10,
    color: "#EC2A3F",
  },
  {
    name: "high",
    value: 2,
    score: 8,
    color: "#FF7507",
  },
  {
    name: "medium",
    value: 1,
    score: 6,
    color: "#F7BB00",
  },
  {
    name: "low",
    value: 0,
    score: 3,
    color: "#23BB8A",
  },
];

export const SeverityFieldsEnum: SeverityFieldsEnumType = {
  NAME: "name",
  VALUE: "value",
  COLOR: "color",
  TEXT_COLOR: "textColor",
  SCORE: "score",
};

const useSeverityAsPriority = () => {
  const {
    appPreferences: { severityAsPriority, severityAsPriorityMapping },
  } = useContext(AppPreferencesContext) as AppPreferencesContextType;

  // The Severity/Priority mapping to be used in the app
  const currentSeverityMapping = useMemo(() => {
    const mapping = severityAsPriority
      ? severityAsPriorityMapping?.filter(
          (mappingLevel: SeverityMappingItem) => !!mappingLevel?.enabled
        )
      : severityMapping;
    return mapping?.sort(
      (a: SeverityMappingItem, b: SeverityMappingItem) => b.value - a.value
    );
  }, [severityAsPriorityMapping, severityAsPriority]);

  const defaultSeverityMapping = useMemo(() => {
    const mapping = severityAsPriority
      ? severityAsPriorityMapping
      : severityMapping;
    return mapping?.sort(
      (a: SeverityMappingItem, b: SeverityMappingItem) => b.value - a.value
    );
  }, [severityAsPriorityMapping, severityAsPriority]);

  const severityDisplay = useMemo(
    () =>
      severityAsPriority
        ? severitiesLabels.PRIORITY
        : severitiesLabels.SEVERITY,
    [severityAsPriority]
  );

  const scoreDisplay = useMemo(
    () =>
      severityAsPriority ? severitiesLabels.PRIORITY : severitiesLabels.SCORE,
    [severityAsPriority]
  );

  const scoresOptions = useMemo(
    () =>
      !!severityAsPriority
        ? currentSeverityMapping?.map((p: SeverityMappingItem) => ({
            severity: p.value,
            score: p.score,
          }))
        : scores,
    [currentSeverityMapping, severityAsPriority]
  );

  const getSeverityField = useCallback(
    (value: string | number | undefined, field: string): any =>
      currentSeverityMapping?.find(
        (s: SeverityMappingItem) =>
          s.value === value || s.value.toString() === value
      )?.[field as keyof SeverityMappingItem],
    [currentSeverityMapping]
  );

  const getScore = useCallback(
    (value: string | number): number | undefined => {
      // we used to return NaN in some cases, but that's not a valid score, so we return undefined now.
      // null is intentionally NOT special-cased — Number(null) === 0, so a
      // null score continues to render LOW (unchanged behavior).
      if (value === undefined || value === "") {
        return undefined;
      }
      const num = severityAsPriorityMapping
        ? Number(value)
        : Math.floor(Number(value));
      return Number.isNaN(num) ? undefined : num;
    },
    [severityAsPriorityMapping]
  );

  // The lowest score that still maps to the given severity level. In severity
  // mode we apply this "floor" so that bumping a finding to a severity level
  // sets the minimum score within that band rather than its representative
  // value. In priority mode the mapping carries an explicit per-level score, so
  // we fall back to that.
  const getSeverityScoreFloor = useCallback(
    (value: string | number): number => {
      if (severityAsPriority) {
        return getSeverityField(value, SeverityFieldsEnum.SCORE) ?? 0;
      }

      const severityValue = Number(value);
      const scoresForSeverity = scores
        .filter((s: ScoreItem) => s.severity === severityValue)
        .map((s: ScoreItem) => s.score);

      return scoresForSeverity.length ? Math.min(...scoresForSeverity) : 0;
    },
    [severityAsPriority, getSeverityField]
  );

  return {
    severityDisplay,
    scoreDisplay,
    scoresOptions,
    getSeverityField,
    getSeverityScoreFloor,
    currentSeverityMapping,
    getScore,
    severityAsPriorityActive: severityAsPriority,
    defaultSeverityMapping,
  };
};

export default useSeverityAsPriority;
